// SupportTeamPanel -- one-line strip on CustomerDetail that shows the
// CC-group contacts for the customer's fuel_brand. Copy-per-contact
// buttons and an "Email all" mailto: that pre-fills TO=customer,
// CC=all group members. No backend beyond the existing GET /api/cc-groups
// -- the brand -> group mapping is done here in the client.
//
// Dave 2026-07-09: needed a fast way to loop the Parkland-managed
// contacts in on ad-hoc "help me with this Chevron/Ultramar/etc site"
// emails without opening Settings, copying, and pasting. Bulloch-side
// customers get the bulloch_only group the same way.

import { useEffect, useState } from 'react';
import axios from 'axios';

// Kept in sync with bullochController.js. Matched case- and
// space-insensitively so 'Fast Gas', 'FastGas' and 'FasGas' all resolve
// to Parkland -- otherwise a spelling variant falls through to Bulloch.
function isParklandBrand(fuelBrand) {
  const s = String(fuelBrand || '').toLowerCase().replace(/\s+/g, '');
  return s.includes('chevron') || s.includes('ultramar') || s.includes('pioneer')
      || s.includes('fasgas')  || s.includes('fastgas');
}

// PetroCan / Suncor sites -> Jim Schroder (single-member group).
// Matches the substring check in bullochController.js's auto-upgrade
// path so a fuel_brand like "Petro Canada" or "Suncor" both resolve.
function isPetroCanBrand(fuelBrand) {
  const lc = String(fuelBrand || '').toLowerCase();
  return lc.includes('petro canada') || lc.includes('petrocan') || lc.includes('suncor');
}

function groupForFuelBrand(fuelBrand) {
  if (isPetroCanBrand(fuelBrand))       return 'petrocan_jim';
  if (isParklandBrand(fuelBrand))       return 'parkland';
  return 'bulloch_only';
}

function groupLabel(group) {
  if (group === 'parkland')      return 'Parkland';
  if (group === 'petrocan_jim')  return 'PetroCan';
  return 'Bulloch';
}

// Small inline copy button. Falls back silently if the clipboard API
// isn't available (older browsers, some non-HTTPS contexts).
function CopyChip({ label, value, primary }) {
  const [copied, setCopied] = useState(false);
  function copy(e) {
    e.preventDefault();
    e.stopPropagation();
    try {
      navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (_) { /* silent */ }
  }
  return (
    <button
      type="button"
      onClick={copy}
      title={`Click to copy ${value}`}
      style={{
        border: 'none',
        background: 'transparent',
        padding: '2px 4px',
        cursor: 'pointer',
        color: 'var(--color-text)',
        fontSize: 'var(--text-small)',
        fontFamily: 'var(--font-sans)'
      }}
    >
      {label}
      {primary && <span title="Primary contact" style={{ marginLeft: 3, color: 'var(--color-warn)' }}>*</span>}
      {copied && <span style={{ marginLeft: 4, color: 'var(--color-good)', fontFamily: 'var(--font-mono)', fontSize: 10 }}>copied</span>}
    </button>
  );
}

export default function SupportTeamPanel({ customer, site }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Site fuel_brand wins when the panel is scoped to a specific site
  // (multi-brand customers have different support teams per location).
  // Falls back to customer.fuel_brand for the customer-level usage.
  // Dave 2026-07-09: 2-site customer with one Esso + one Chevron site
  // needed each to show its own group, not the customer's primary.
  const fuelBrand = site?.fuel_brand || customer?.fuel_brand || '';
  const group = groupForFuelBrand(fuelBrand);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get('/api/cc-groups')
      .then((r) => {
        if (cancelled) return;
        const filtered = (r.data || [])
          .filter((c) => c.group_name === group && c.active)
          .sort((a, b) => {
            // Primary contact first, then alpha by name/email.
            if (a.is_primary && !b.is_primary) return -1;
            if (b.is_primary && !a.is_primary) return 1;
            const an = (a.contact_name || a.email || '').toLowerCase();
            const bn = (b.contact_name || b.email || '').toLowerCase();
            return an.localeCompare(bn);
          });
        setContacts(filtered);
      })
      .catch(() => { /* fail-soft: hide the panel on error */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [group]);

  if (loading) return null;
  if (contacts.length === 0) return null;

  // mailto: expects comma-separated addresses. Pre-fill TO with the
  // customer's primary email so a click drops straight into a compose
  // window with everyone already looped in.
  const to = customer?.email1 || '';
  const ccList = contacts.map((c) => c.email).filter(Boolean);
  const mailto = `mailto:${encodeURIComponent(to)}?cc=${encodeURIComponent(ccList.join(','))}`;

  // Fuel-brand suffix only shown when we know it -- avoids the awkward
  // "(Bulloch -- )" empty parenthetical for customers without a brand.
  const groupSuffix = fuelBrand ? ` -- ${fuelBrand}` : '';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        padding: '8px 12px',
        margin: '0 0 var(--space-3)',
        background: 'var(--color-surface-sunken, #f8fafc)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
        flexWrap: 'wrap',
        fontSize: 'var(--text-small)'
      }}
    >
      <span style={{
        color: 'var(--color-text-dim)',
        fontFamily: 'var(--font-mono)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        fontSize: 11
      }}>
        Support Team ({groupLabel(group)}{groupSuffix}):
      </span>
      <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', flex: 1 }}>
        {contacts.map((c, i) => (
          <span key={c.id} style={{ display: 'inline-flex', alignItems: 'center' }}>
            {i > 0 && <span style={{ color: 'var(--color-text-faint)', margin: '0 2px' }}>|</span>}
            <CopyChip
              label={c.contact_name || c.email}
              value={c.email}
              primary={!!c.is_primary}
            />
          </span>
        ))}
      </span>
      <a
        href={mailto}
        className="btn-secondary btn-sm"
        style={{ whiteSpace: 'nowrap', textDecoration: 'none' }}
        title={`Opens your mail client with TO=${to || '(no customer email)'}, CC=${ccList.join(', ')}`}
      >
        Email all
      </a>
    </div>
  );
}
